import {
  Directive,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  Signal,
  signal,
  SimpleChanges,
  WritableSignal,
} from '@angular/core';
import { CustomDomEvents, CustomEvents } from '@utils/events/custom-events';
import { EventContext } from '@utils/events/event-context';
import { firstValueFrom, Subject } from 'rxjs';
import { BusinessObjectsElementService, DocumentElementResponse, ElementsResult } from 'src/openapi-client';
import { LOG } from '../common/util/log';
import { ElementContext } from './element-context';

/**
 * Fetches elements from a Business Objects document by a document cuid and a report index.
 * Fetched elements can be accessed by name with the {@link ElementPipe }.
 * Advanced access to elements is provided by injecting {@link ElementContext}.
 *
 * @example Using directive
 * ```html
 * <div provideElements [documentId]="someDocumentCuid" [reportIndex]="1"">
 * ```
 *
 * @example Using pipe
 * ```html
 * <element-host [element]="'elementName' | element | async"/>
 * ```
 *
 * @example Using injection
 * ```ts
 * class Component {
 *   private elements = inject(ElementContext);
 * }
 * ```
 */
@Directive({ selector: '[provideElements]', standalone: true, providers: [ElementContext] })
export class ProvideElementsDirective implements OnChanges {
  @Input({ required: true }) public documentId: string;
  @Input({ required: true }) public reportIndex: number;

  @Input({ required: false }) public funds: string | undefined = undefined;
  @Input({ required: false }) public parameters: Record<string, string> | undefined;
  @Input({ required: false }) public autoupdate: boolean = true;

  @Output() public elementsLoaded: Subject<number> = new EventEmitter();
  @Output() public elementLoadError: Subject<void> = new EventEmitter();

  public readonly loading: Signal<boolean> = signal(false);
  public readonly elementContext: ElementContext = inject(ElementContext);

  protected readonly elementService = inject(BusinessObjectsElementService);
  private readonly eventContext? = inject(EventContext, { optional: true });
  private readonly elementRef: ElementRef<HTMLElement> = inject(ElementRef);

  protected content: DocumentElementResponse[] = [];

  private requestId: number = 0;

  constructor() {
    this.elementContext.loading = this.loading;
  }

  public ngOnChanges({ documentId, report, funds, parameters }: SimpleChanges): void {
    if (!this.autoupdate || (!documentId && !report && !funds && !parameters)) {
      return;
    }

    this.update();
  }

  public ngOnDestroy(): void {
    this.elementLoadError.complete();
    this.elementsLoaded.complete();
  }


  public clear(): void {
    this.elementContext.clear();
  }

  public async update(): Promise<void> {
    const currentRequestId: number = ++this.requestId;
    try {
      this.elementContext.setMetadata(undefined);
      (this.loading as WritableSignal<boolean>).set(true);
      const result: ElementsResult = await firstValueFrom(
        this.elementService.getElements(this.documentId, this.reportIndex, this.funds, this.parameters)
      );

      if (this.requestId !== currentRequestId) {
        return;
      }

      this.content = result.elements;
      this.elementContext.setMetadata(result.metadata ?? null);
      this.elementContext.setElementsReadonly(result.elements);

      this.elementsLoaded.next(result.elements.length);
      this.eventContext?.dispatch(CustomEvents.ElementsLoaded, { count: result.elements.length, source: this });
    } catch (e) {
      this.eventContext?.dispatch(CustomEvents.ElementLoadError, null);
      this.elementLoadError.next();
      LOG.error(e);

      const errorEvent = new CustomEvent<Error>(CustomDomEvents.ELEMENT_LOAD_ERROR, { detail: e, bubbles: true });
      this.elementRef.nativeElement.dispatchEvent(errorEvent);
    } finally {
      if (this.requestId == currentRequestId) {
        (this.loading as WritableSignal<boolean>).set(false);
      }
    }
  }
}
